import { SocialChannels } from "@contracts/socialNetworks";
import type { SocialChannelStatus } from "@contracts/shareStop";

export interface SocialChannelData {
  channelType: SocialChannels;
  channelId: string; // e.g., Facebook Page ID or Instagram Business Account ID
  username?: string | null;
  displayName?: string | null;
  status?: SocialChannelStatus | string | null;
  /** Instagram-only: Bundle connection method ("FACEBOOK" or "INSTAGRAM"). */
  instaAuthMode?: string | null;
}

export interface DaySlot {
  // .NET DayOfWeek: Sunday = 0, Monday = 1, ... Saturday = 6
  day: number;
  // Represented as HH:mm:ss string from server; we commonly edit HH:mm
  time: string;
}

export interface PostingSchedule {
  schedule?: DaySlot[];
}

/** Matches backend InterestGenerationStatus */
export type InterestGenerationStatus = "NotStarted" | "InProgress" | "Completed" | "Failed";

/** Cosmos `*_social-settings` row (system-owned). */
export interface ContentCalendarSystemSettingsData {
  id: string;
  customerId: string;
  calendarId: string;
  bundleSocialTeamId: string | null;
  socialChannels: SocialChannelData[];
  currentOperation?: string | null;
  operationStatus?: unknown;
  firstPostGenWeek?: string | null;
  nextPostGenStartWeek?: string | null;
  lastViewed?: string | null;
  createdDate?: string;
  createdBy?: string;
  updatedDate?: string;
  updatedBy?: string;
}

/** Cosmos `*_cal-customize` row (user-editable calendar fields). */
export interface ContentCalendarUserSettingsData {
  id: string;
  customerId: string;
  calendarId: string;
  calendarName?: string | null;
  postingSchedule: PostingSchedule;
  postsPerWeek: number;
  mediaPerPost: number;
  weeksToGenPosts: number;
  daysEarlyToGeneratePosts: number;
  timeUntilPhotosCanBeReused?: string;
  timeUntilContentBucketsCanBeReused?: string;
  interestGenStatus?: InterestGenerationStatus | null;
  enableInterests?: boolean;
  enableLocalEvents?: boolean;
  enableThemes?: boolean;
  /** When true, scheduled weekly post generation skips this calendar. */
  disableWeeklyGen?: boolean;
  /** When true, new AI posts still get associated locations, but mention and Instagram tag defaults stay off. */
  disableAutoTagging?: boolean;
  excludeFromIndex?: {
    brandVoice?: string | null;
    weeklyCadence?: string | null;
    customerContext?: string | null;
    interests?: string | null;
    interestsDebug?: string | null;
  };
  createdDate?: string;
  createdBy?: string;
  updatedDate?: string;
  updatedBy?: string;
}

export interface TimeZoneOption {
  name: string;
  zoneId: string;
}

export const COMMON_US_TIMEZONES: TimeZoneOption[] = [
  { name: "Eastern Time", zoneId: "America/New_York" },
  { name: "Central Time", zoneId: "America/Chicago" },
  { name: "Mountain Time", zoneId: "America/Denver" },
  { name: "Arizona", zoneId: "America/Phoenix" },
  { name: "Pacific Time", zoneId: "America/Los_Angeles" },
];

export interface SocialMediaSettingsV2Response {
  settings: ContentCalendarSystemSettingsData;
  userSettings: ContentCalendarUserSettingsData;
  timeZone: string | null;
}

export interface UpdateSocialSettingsV2Request {
  userSettings: ContentCalendarUserSettingsData;
  timeZone: string | null;
}

/** Deep-clone user settings from API and normalize slot times to HH:mm for `<input type="time">`. */
export function cloneUserSettingsForEditing(u: ContentCalendarUserSettingsData): ContentCalendarUserSettingsData {
  const copy = JSON.parse(JSON.stringify(u)) as ContentCalendarUserSettingsData;
  copy.excludeFromIndex ??= {};
  copy.postingSchedule ??= { schedule: [] };
  if (copy.postingSchedule.schedule?.length) {
    copy.postingSchedule.schedule = copy.postingSchedule.schedule.map(slot => ({
      ...slot,
      time: (slot.time || "10:00:00").slice(0, 5),
    }));
  }
  if (copy.weeksToGenPosts == null) copy.weeksToGenPosts = 2;
  if (copy.disableWeeklyGen == null) copy.disableWeeklyGen = false;
  if (copy.disableAutoTagging == null) copy.disableAutoTagging = false;
  return copy;
}
